import ExcelJS from "exceljs";
import axiosInstance from "./api";
import toast from "react-hot-toast";

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDatesBetween = (start, end) => {
  const dates = [];
  const current = new Date(start);
  const last = new Date(end);

  current.setUTCHours(12, 0, 0, 0);
  last.setUTCHours(12, 0, 0, 0);

  while (current <= last) {
    dates.push(new Date(current));
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return dates;
};

export const handleDownloadTemplate = async (eventId) => {
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Stand Production");

    const event = await axiosInstance.get(`/event/${eventId}`);
    const { buildUp, showDays, teardown, name } = event.data.event;

    const activitiesRes = await axiosInstance.get(`/activity/event/${eventId}`);
    const fetchedActivities = activitiesRes.data.activities || [];

    const uniqueActivityNames = [...new Set(fetchedActivities.map(a => a.name))];
    const activityNames = uniqueActivityNames.length > 0 ? uniqueActivityNames : [
      "Arrival onsite",
      "Material offloading",
      "Platform build",
      "Metal work",
      "Structure build",
      "Painting",
      "Lighting setup",
      "AV setup",
      "Graphics",
      "Equipment setup",
      "AV testing",
      "Equipment testing",
      "Furniture setup",
      "Stand cleaning",
    ];

    const buildUpDates = getDatesBetween(new Date(buildUp.start), new Date(buildUp.end));
    const showDates = getDatesBetween(new Date(showDays.start), new Date(showDays.end));
    const teardownDates = getDatesBetween(new Date(teardown.start), new Date(teardown.end));

    const allDates = [...buildUpDates, ...showDates, ...teardownDates];

    const formatDateRange = (dates) => {
      if (dates.length === 0) return "";
      const start = dates[0];
      const end = dates[dates.length - 1];
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${monthNames[start.getMonth()]} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
    };

    const eventDateRange = formatDateRange(allDates);
    const buildUpRange = formatDateRange(buildUpDates);
    const teardownRange = formatDateRange(teardownDates);

    // Calculate total columns: 2 (A+B for activity) + all dates + empty columns to reach 30
    const dateColumnsCount = allDates.length;
    const totalCols = Math.max(30, 2 + dateColumnsCount);

    // ======= TITLE ROW =======
    worksheet.mergeCells(1, 1, 1, totalCols);
    const titleCell = worksheet.getCell("A1");
    titleCell.value = `${name || "EVENT"} - Stand Production Schedule`;
    titleCell.font = { size: 18, bold: true, color: { argb: "FFFFFFFF" } };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1F4E78" },
    };
    titleCell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
    worksheet.getRow(1).height = 30;

    // ======= INFO ROWS (2-5) with borders =======
    const infoStyle = {
      font: { bold: true, color: { argb: "FFFFFFFF" } },
      fill: {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1F4E78" },
      },
      alignment: { vertical: "middle" },
      border: {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      }
    };

    const valueStyle = {
      fill: {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFFFF" },
      },
      alignment: { vertical: "middle" },
      border: {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      }
    };

    // Row 2: Event Dates and Exhibitor Company Name
    worksheet.mergeCells(2, 1, 2, 2);
    worksheet.getCell(2, 1).value = "Event Dates:";
    Object.assign(worksheet.getCell(2, 1), infoStyle);

    worksheet.mergeCells(2, 3, 2, 13);
    worksheet.getCell(2, 3).value = eventDateRange;
    Object.assign(worksheet.getCell(2, 3), valueStyle);

    worksheet.mergeCells(2, 14, 2, 23);
    worksheet.getCell(2, 14).value = "Exhibitor Company Name:";
    Object.assign(worksheet.getCell(2, 14), infoStyle);

    worksheet.mergeCells(2, 24, 2, 30);
    worksheet.getCell(2, 24).value = "ABC Company";
    Object.assign(worksheet.getCell(2, 24), valueStyle);

    // Row 3: Build Up Dates and Stand Number
    worksheet.mergeCells(3, 1, 3, 2);
    worksheet.getCell(3, 1).value = "Build Up Dates:";
    Object.assign(worksheet.getCell(3, 1), infoStyle);

    worksheet.mergeCells(3, 3, 3, 13);
    worksheet.getCell(3, 3).value = buildUpRange;
    Object.assign(worksheet.getCell(3, 3), valueStyle);

    worksheet.mergeCells(3, 14, 3, 23);
    worksheet.getCell(3, 14).value = "Stand Number:";
    Object.assign(worksheet.getCell(3, 14), infoStyle);

    worksheet.mergeCells(3, 24, 3, 30);
    worksheet.getCell(3, 24).value = "Hall 1, Stand 2C 10.";
    Object.assign(worksheet.getCell(3, 24), valueStyle);

    // Row 4: Teardown Dates and Contact Person
    worksheet.mergeCells(4, 1, 4, 2);
    worksheet.getCell(4, 1).value = "Teardown Dates:";
    Object.assign(worksheet.getCell(4, 1), infoStyle);

    worksheet.mergeCells(4, 3, 4, 13);
    worksheet.getCell(4, 3).value = teardownRange;
    Object.assign(worksheet.getCell(4, 3), valueStyle);

    worksheet.mergeCells(4, 14, 4, 23);
    worksheet.getCell(4, 14).value = "Contact Person:";
    Object.assign(worksheet.getCell(4, 14), infoStyle);

    worksheet.mergeCells(4, 24, 4, 30);
    worksheet.getCell(4, 24).value = "";
    Object.assign(worksheet.getCell(4, 24), valueStyle);

    // Row 5: Venue and Contact Number
    worksheet.mergeCells(5, 1, 5, 2);
    worksheet.getCell(5, 1).value = "Venue:";
    Object.assign(worksheet.getCell(5, 1), infoStyle);

    worksheet.mergeCells(5, 3, 5, 13);
    worksheet.getCell(5, 3).value = "Bab Iddid, Marrakech, Morocco";
    Object.assign(worksheet.getCell(5, 3), valueStyle);

    worksheet.mergeCells(5, 14, 5, 23);
    worksheet.getCell(5, 14).value = "Contact Number:";
    Object.assign(worksheet.getCell(5, 14), infoStyle);

    worksheet.mergeCells(5, 24, 5, 30);
    worksheet.getCell(5, 24).value = "";
    Object.assign(worksheet.getCell(5, 24), valueStyle);

    [2, 3, 4, 5].forEach(rowNum => {
      worksheet.getRow(rowNum).height = 20;
    });

    // ======= ROW 6: PHASE LABELS with borders =======
    worksheet.getRow(6).height = 20;

    const phaseLabelStyle = {
      font: { bold: true, color: { argb: "FFFFFFFF" } },
      alignment: { horizontal: "center", vertical: "middle" },
      fill: {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1F4E78" },
      },
      border: {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      }
    };

    const buildUpStartCol = 3;
    const showDaysStartCol = buildUpStartCol + buildUpDates.length;
    const teardownStartCol = showDaysStartCol + showDates.length;
    const lastDateCol = 2 + dateColumnsCount;

    // Build Up label
    if (buildUpDates.length > 0) {
      worksheet.mergeCells(6, buildUpStartCol, 6, buildUpStartCol + buildUpDates.length - 1);
      const buildUpLabel = worksheet.getCell(6, buildUpStartCol);
      buildUpLabel.value = "Build Up";
      buildUpLabel.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10 };
      Object.assign(buildUpLabel, phaseLabelStyle);
    }

    // Show Days label
    if (showDates.length > 0) {
      worksheet.mergeCells(6, showDaysStartCol, 6, showDaysStartCol + showDates.length - 1);
      const showLabel = worksheet.getCell(6, showDaysStartCol);
      showLabel.value = "Show days";
      showLabel.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 9 };
      Object.assign(showLabel, phaseLabelStyle);
    }

    // Teardown label
    if (teardownDates.length > 0) {
      worksheet.mergeCells(6, teardownStartCol, 6, teardownStartCol + teardownDates.length - 1);
      const teardownLabel = worksheet.getCell(6, teardownStartCol);
      teardownLabel.value = "Teardown";
      teardownLabel.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10 };
      Object.assign(teardownLabel, phaseLabelStyle);
    }

    const headerRowNumber = 7;
    worksheet.getRow(headerRowNumber).height = 50;

    worksheet.getColumn(1).width = 6;
    worksheet.getColumn(2).width = 25;

    // Merge A6:B7 into ONE big header box
    worksheet.mergeCells(6, 1, 7, 2);
    const activityHeaderCell = worksheet.getCell(6, 1);

    activityHeaderCell.value =
      "ACTIVITY\n(Select activities and timeline\nactivities can be added as required)";

    activityHeaderCell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 9 };
    activityHeaderCell.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };

    activityHeaderCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1F4E78" },
    };

    activityHeaderCell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };

    // Dates start from column 3 (C)
    let colIndex = 3;

    // Build Up dates
    buildUpDates.forEach((date) => {
      const cell = worksheet.getRow(headerRowNumber).getCell(colIndex);
      cell.value = date;
      cell.numFmt = "dd-mmm";
      cell.alignment = { horizontal: "center", vertical: "bottom", textRotation: 90 };
      cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 9 };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1F4E78" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      worksheet.getColumn(colIndex).width = 3.5;
      colIndex++;
    });

    // Show Days dates
    showDates.forEach((date) => {
      const cell = worksheet.getRow(headerRowNumber).getCell(colIndex);
      cell.value = date;
      cell.numFmt = "dd-mmm";
      cell.alignment = { horizontal: "center", vertical: "bottom", textRotation: 90 };
      cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 9 };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1F4E78" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      worksheet.getColumn(colIndex).width = 3.5;
      colIndex++;
    });

    // Teardown dates
    teardownDates.forEach((date) => {
      const cell = worksheet.getRow(headerRowNumber).getCell(colIndex);
      cell.value = date;
      cell.numFmt = "dd-mmm";
      cell.alignment = { horizontal: "center", vertical: "bottom", textRotation: 90 };
      cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 9 };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1F4E78" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      worksheet.getColumn(colIndex).width = 3.5;
      colIndex++;
    });

    // ======= ACTIVITIES LIST (Starting Row 8) =======
    const startRow = 8;

    activityNames.forEach((name, i) => {
      const row = worksheet.getRow(startRow + i);

      // Column A: Activity index with blue bg
      const indexCell = row.getCell(1);
      indexCell.value = i + 1;
      indexCell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      indexCell.alignment = { horizontal: "center", vertical: "middle" };
      indexCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1F4E78" },
      };
      indexCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      // Column B: Activity name
      const cell = row.getCell(2);
      cell.value = name;
      cell.alignment = { vertical: "middle", indent: 1 };
      row.height = 18;

      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      // Build Up columns - individual cells (starting from column 3)
      for (let c = buildUpStartCol; c < showDaysStartCol; c++) {
        const dataCell = row.getCell(c);
        dataCell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        dataCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFFFFF" },
        };
      }

      // Show Days - MERGE all columns into one cell per activity
      if (showDates.length > 0) {
        worksheet.mergeCells(
          startRow + i,
          showDaysStartCol,
          startRow + i,
          showDaysStartCol + showDates.length - 1
        );

        const showDaysCell = row.getCell(showDaysStartCol);

        showDaysCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFFF00" }, // Yellow
        };

        showDaysCell.alignment = { horizontal: "center", vertical: "middle" };

        showDaysCell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      }

      // Teardown columns - individual cells (only up to last date column)
      for (let c = teardownStartCol; c <= lastDateCol; c++) {
        const dataCell = row.getCell(c);
        dataCell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        dataCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF000000" }, // Black
        };
      }
    });

    // ======= HOW TO FILL INSTRUCTIONS =======
    const instructionsStartRow = startRow + activityNames.length + 2;

    worksheet.getCell(`A${instructionsStartRow}`).value = "HOW TO FILL THIS TEMPLATE:";
    worksheet.getCell(`A${instructionsStartRow}`).font = { bold: true, size: 12, color: { argb: "FF1F4E78" } };

    const instructions = [
      "1. Find the activity you plan to perform in the ACTIVITY column",
      "2. Locate the date(s) when you will perform this activity in the date columns",
      "3. Fill the cell with GREEN color to mark when the activity will be done",
      "4. You can mark multiple dates for the same activity if it spans several days",
      "5. Show Days (yellow) are for reference - stands must be complete before show days begin"
    ];

    instructions.forEach((instruction, i) => {
      const cell = worksheet.getCell(`A${instructionsStartRow + 1 + i}`);
      cell.value = instruction;
      cell.alignment = { wrapText: true, vertical: "top", indent: 1 };
      cell.font = { size: 10 };
    });

    const instructionsEndCol = Math.min(lastDateCol, 15);
    instructions.forEach((instruction, i) => {
      worksheet.mergeCells(instructionsStartRow + 1 + i, 1, instructionsStartRow + 1 + i, instructionsEndCol);
    });

    // ======= IMPORTANT NOTES SECTION =======
    const notesStartRow = instructionsStartRow + instructions.length + 2;

    worksheet.getCell(`A${notesStartRow}`).value = "IMPORTANT NOTES:";
    worksheet.getCell(`A${notesStartRow}`).font = { bold: true, size: 11 };

    const notes = [
      `1. Stands must be COMPLETELY ready by ${showDates[0] ? formatLocalDate(showDates[0]) : "show start date"}`,
      "2. Unless approved by the Operations Manager, there is no stand work on April 13, 2025 as this is the master clean of the whole venue",
      "3. Floor Managers & the Operations Manager will closely monitor the stand production schedule submitted by the exhibitor/contractor",
      "4. During build up, throwing & sweeping of stand rubbish & waste onto the main aisles is STRICTLY PROHIBITED"
    ];

    notes.forEach((note, i) => {
      worksheet.getCell(`A${notesStartRow + 1 + i}`).value = note;
      worksheet.getCell(`A${notesStartRow + 1 + i}`).alignment = { wrapText: true, vertical: "top" };
    });

    const notesEndCol = Math.min(lastDateCol, 15);
    notes.forEach((note, i) => {
      worksheet.mergeCells(notesStartRow + 1 + i, 1, notesStartRow + 1 + i, notesEndCol);
    });

    // ======= DOWNLOAD FILE =======
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name || "event"}-retro-plan-template.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success("Retro plan template downloaded");
  } catch (err) {
    console.error(err);
    toast.error("Failed to generate template");
  }
};